<script setup>
import { DEFAULT_SIMPLE_HEIGHT, DEFAULT_OXER_HEIGHTS, DEFAULT_TRIPLE_HEIGHTS } from '@/components/Ajuda/Montador/lib/defaultParametros'
import { reactive, ref, computed, watch, nextTick } from 'vue'
import {
  parseNumberList,
  formatListSemicolon,
  normalizeListInputLive,
  finalizeListInput,
  heightsToGaps,
  formatDecimalBR,
} from '@/components/Ajuda/Montador/lib/numbers'

// Props / Emits
const props = defineProps({
  open:   { type: Boolean, required: true },
  // passa o objeto params reativo atual (para prefill)
  params: { type: Object,  required: true },
})
const emit = defineEmits(['close', 'save'])

// Estado da UI do modal
const tab = ref('arena')                 // 'arena' | 'obstaculos' | 'percurso' | 'combos' | 'visual'
const typeTab = ref('oxer')              // para aba Obstáculos: 'oxer' | 'tripleBar'
const flags = reactive({
  applySelected: false,
  applyType: false,
  applyAll:  false,
  applyArenaNow: false,
})

// Defaults globais de altura



// Editável (clone)
const edit = reactive(structuredClone(props.params || {}))

// --- defaults para evitar "Cannot read properties of undefined" ---
function ensureModalDefaults(e) {
  e.free   = e.free   || {};
  e.arena  = e.arena  || {};
  e.visual = e.visual || {};

  if (typeof e.free.showHandles !== 'boolean') e.free.showHandles = false;
  if (typeof e.free.confine     !== 'boolean') e.free.confine     = false;

  if (typeof e.arena.applyNow   !== 'boolean') e.arena.applyNow   = false;

  if (typeof e.visual.showRulers !== 'boolean') e.visual.showRulers = true;
  e.visual.rulerStep = Number.isFinite(+e.visual.rulerStep) ? +e.visual.rulerStep : 10;
}

// 1ª chamada: garante subobjetos/valores na abertura do modal
ensureModalDefaults(edit)

// Quando o modal receber novos params, atualize e normalize de novo
watch(() => props.params, (p) => {
  Object.assign(edit, JSON.parse(JSON.stringify(p || {})))
  ensureModalDefaults(edit)
}, { deep: true })


// coleta alturas a partir de várias chaves/formatos
function collectHeights(src = {}) {
  const out = []
  if (Array.isArray(src.heights))        out.push(...src.heights)
  if (Array.isArray(src.barHeights))     out.push(...src.barHeights)
  if (Array.isArray(src.alturas))        out.push(...src.alturas)
  if (Array.isArray(src?.bars?.heights)) out.push(...src.bars.heights)

  const tryStr = (v) => {
    if (typeof v === 'string' && v.trim()) {
      const arr = parseNumberList(v) || []
      out.push(...arr)
    }
  }
  tryStr(src.heights)
  tryStr(src.barHeights)
  tryStr(src.alturas)
  tryStr(src.heightsStr)
  tryStr(src.alturasStr)

  const seen = new Set()
  const unique = []
  for (const n of out.map(Number).filter(Number.isFinite)) {
    const key = n.toFixed(3)
    if (!seen.has(key)) { seen.add(key); unique.push(n) }
  }
  return unique
}

// normaliza => garante obstacles[*].heights[]; NÃO cria a partir de hm
function normalizeObstacleHeightsInParams(p) {
  if (!p?.obstacles) return
  for (const [k, src] of Object.entries(p.obstacles)) {
    const cur = { ...(src || {}) }
    const arr = collectHeights(cur)
    if (arr.length) cur.heights = arr

    // cleanup de aliases
    delete cur.barHeights
    delete cur.alturas
    delete cur.heightsStr
    delete cur.alturasStr
    if (cur.bars && Array.isArray(cur.bars.heights)) delete cur.bars.heights

    p.obstacles[k] = cur
  }
}



// Strings editáveis (listas)
const lists = reactive({
  oxerThicks:   '',   // "0,10;0,10"
  oxerGaps:     '',   // "0,60"
  oxerHeights:  '',   // "0,60;0,80"
  tripleThicks: '',
  tripleGaps:   '',
  tripleHeights:''
})

// Subtipos disponíveis para a aba "Obstáculos"
const typeOptions = [
  { value: 'vertical',  label: 'Vertical' },
  { value: 'oxer',      label: 'Oxer' },
  { value: 'tripleBar', label: 'Triple Bar' },
  { value: 'gate',      label: 'Gate' },
  { value: 'plank',     label: 'Plank' },
  { value: 'wall',      label: 'Muro' },
  { value: 'start',     label: 'Start' },
  { value: 'finish',    label: 'Finish' },
]

// Valor exibido ao lado do slider (passo da régua)
const rulerStepLabel = computed(() =>
  formatDecimalBR((edit.visual?.rulerStep ?? 1), 2)
)

// Abre: clona params e preenche strings
function prepareFromParams () {
  Object.assign(edit, structuredClone(props.params || {}))

  // sinônimo legado: se existir 'muro' e não 'wall', espelha
  edit.obstacles = edit.obstacles || {}
  if (edit.obstacles.muro && !edit.obstacles.wall) {
    edit.obstacles.wall = structuredClone(edit.obstacles.muro)
  }

  // garante objetos por tipo e defaults (SIMPLES = 1,2)
  const ALL_TYPES = ['vertical','oxer','tripleBar','gate','plank','wall','start','finish']
  const SIMPLE_TYPES = ['vertical','gate','plank','wall']
  for (const t of ALL_TYPES) {
    if (!edit.obstacles[t]) edit.obstacles[t] = {}
    if (SIMPLE_TYPES.includes(t)) {
      const H = Array.isArray(edit.obstacles[t].heights) ? edit.obstacles[t].heights : []
      edit.obstacles[t].heights = H.length ? H.map(Number) : [DEFAULT_SIMPLE_HEIGHT]
    }
  }

  // garante objetos por tipo
  edit.obstacles = edit.obstacles || {}
  for (const t of typeOptions.map(o => o.value)) {
    if (!edit.obstacles[t]) edit.obstacles[t] = {}
  }

  // strings com ; e vírgula decimal
  lists.oxerThicks  = formatListSemicolon(edit.obstacles.oxer.thicknesses || [], 2)
  lists.oxerGaps    = formatListSemicolon(edit.obstacles.oxer.gaps || [], 2)
  {
    const H = edit.obstacles.oxer.barHeights || edit.obstacles.oxer.heights || []
    lists.oxerHeights = formatListSemicolon(H.length ? H : DEFAULT_OXER_HEIGHTS, 2)
  }

  lists.tripleThicks  = formatListSemicolon(edit.obstacles.tripleBar.thicknesses || [], 2)
  lists.tripleGaps    = formatListSemicolon(edit.obstacles.tripleBar.gaps || [], 2)
  {
    const Ht = edit.obstacles.tripleBar.barHeights || edit.obstacles.tripleBar.heights || []
    lists.tripleHeights = formatListSemicolon(Ht.length ? Ht : DEFAULT_TRIPLE_HEIGHTS, 2)
  }

  // defaults visuais (se faltarem)
  if (!edit.visual) edit.visual = {}
  if (typeof edit.visual.showRulers !== 'boolean') edit.visual.showRulers = true
  if (typeof edit.visual.rulerStep !== 'number') edit.visual.rulerStep = 10
}

watch(() => props.open, async (v) => {
  if (v) {
    prepareFromParams()
    await nextTick()
    wireRanges() // pinta sliders
  }
})

// Normalização live/blur nas listas
function onListInput (key, ev) {
  lists[key] = normalizeListInputLive(ev?.target?.value ?? '')
}
function onListBlur (key) {
  lists[key] = finalizeListInput(lists[key])
}

// Validações (somente quando a aba exige)
const oxerError = computed(() => {
  if (tab.value !== 'obstaculos' || typeTab.value !== 'oxer') return ''
  const th = parseNumberList(lists.oxerThicks)
  const gp = parseNumberList(lists.oxerGaps)
  if (th.length !== 2) return 'Oxer precisa de 2 espessuras.'
  if (gp.length !== 1 && !parseNumberList(lists.oxerHeights).length) return 'Informe 1 vão ou as alturas.'
  if (gp.length && gp.some(g => g < 0.30 || g > 2.00)) return 'Vãos entre 0,30 e 2,00 m.'
  return ''
})
const tripleError = computed(() => {
  if (tab.value !== 'obstaculos' || typeTab.value !== 'tripleBar') return ''
  const th = parseNumberList(lists.tripleThicks)
  const gp = parseNumberList(lists.tripleGaps)
  if (th.length !== 3) return 'Triple Bar precisa de 3 espessuras.'
  if (gp.length !== 2 && !parseNumberList(lists.tripleHeights).length) return 'Informe 2 vãos ou as alturas.'
  if (gp.length && gp.some(g => g < 0.30 || g > 2.00)) return 'Vãos entre 0,30 e 2,00 m.'
  return ''
})

// Derivar vãos a partir das alturas
function deriveOxerGaps() {
  const th = parseNumberList(lists.oxerThicks)
  const hh = parseNumberList(lists.oxerHeights)
  if (th.length === 2 && hh.length === 2) {
    const gp = heightsToGaps(hh, th)
    lists.oxerGaps = formatListSemicolon(gp, 2)
  }
}
function deriveTripleGaps() {
  const th = parseNumberList(lists.tripleThicks)
  const hh = parseNumberList(lists.tripleHeights)
  if (th.length === 3 && hh.length === 3) {
    const gp = heightsToGaps(hh, th)
    lists.tripleGaps = formatListSemicolon(gp, 2)
  }
}

// Monta payload para o pai aplicar
function buildPayload() {
  const clone = structuredClone(edit)

  // aplica listas parseds
  clone.obstacles = clone.obstacles || {}
  // OXER
  clone.obstacles.oxer = { ...(clone.obstacles.oxer || {}) }
  clone.obstacles.oxer.thicknesses = parseNumberList(lists.oxerThicks)
  clone.obstacles.oxer.gaps        = parseNumberList(lists.oxerGaps)
  clone.obstacles.oxer.barHeights  = parseNumberList(lists.oxerHeights)
  if (!clone.obstacles.oxer.gaps.length) {
    const t = clone.obstacles.oxer.thicknesses || []
    const H = clone.obstacles.oxer.barHeights || []
    if (t.length === 2 && H.length === 2) {
      clone.obstacles.oxer.gaps = heightsToGaps(H, t)
    }
  }
  if (clone.obstacles.oxer.barHeights.length !== clone.obstacles.oxer.thicknesses.length) {
    clone.obstacles.oxer.barHeights = []
  }

  // defaults de alturas se ainda estiver vazio
  if (!clone.obstacles.oxer.barHeights.length) {
    clone.obstacles.oxer.barHeights = DEFAULT_OXER_HEIGHTS
  }

  // TRIPLE
  clone.obstacles.tripleBar = { ...(clone.obstacles.tripleBar || {}) }
  clone.obstacles.tripleBar.thicknesses = parseNumberList(lists.tripleThicks)
  clone.obstacles.tripleBar.gaps        = parseNumberList(lists.tripleGaps)
  clone.obstacles.tripleBar.barHeights  = parseNumberList(lists.tripleHeights)
  if (!clone.obstacles.tripleBar.gaps.length) {
    const t = clone.obstacles.tripleBar.thicknesses || []
    const H = clone.obstacles.tripleBar.barHeights || []
    if (t.length === 3 && H.length === 3) {
      clone.obstacles.tripleBar.gaps = heightsToGaps(H, t)
    }
  }
  if (clone.obstacles.tripleBar.barHeights.length !== clone.obstacles.tripleBar.thicknesses.length) {
    clone.obstacles.tripleBar.barHeights = []
  }

  if (!clone.obstacles.tripleBar.barHeights.length) {
    clone.obstacles.tripleBar.barHeights = DEFAULT_TRIPLE_HEIGHTS
  }

  // Evita somar 'heights' + 'barHeights' na normalização
  if (clone?.obstacles?.oxer)      delete clone.obstacles.oxer.heights
  if (clone?.obstacles?.tripleBar) delete clone.obstacles.tripleBar.heights

  return {
    params: clone,
    tab: tab.value,
    typeTab: typeTab.value,
    flags: { ...flags },
  }
}

// Botões Salvar
function onSave(mode = 'save') {
  // validações que você já tem...
  if (tab.value === 'obstaculos') {
    if (typeTab.value === 'oxer' && oxerError.value) return alert(oxerError.value)
    if (typeTab.value === 'tripleBar' && tripleError.value) return alert(tripleError.value)
  }

  const built = buildPayload() || {}

  // ✅ garante params.obstacles[<tipo>].heights como lista numérica
  if (built.params) normalizeObstacleHeightsInParams(built.params)
  // manter sinônimos: wall ↔ muro
  if (built.params?.obstacles?.wall) {
    built.params.obstacles.muro = structuredClone(built.params.obstacles.wall)
  } else if (built.params?.obstacles?.muro) {
    built.params.obstacles.wall = structuredClone(built.params.obstacles.muro)
  }

  emit('save', { mode, ...built })
}

function onClose() { emit('close') }

// Slider: tema consistente (atualiza o preenchimento no WebKit)
function rangeSetFill(el) {
  const min = Number(el.min || 0), max = Number(el.max || 100)
  const val = Number(el.value || 0)
  const pct = ((val - min) * 100) / (max - min || 1)
  el.style.setProperty('--percent', pct + '%')
}
function wireRanges() {
  const root = document.querySelector('.params-modal')
  if (!root) return
  root.querySelectorAll('input[type="range"]').forEach(el => {
    rangeSetFill(el)
    el.addEventListener('input', () => rangeSetFill(el), { passive: true })
  })
}
</script>

<template>
  <teleport to="body">
    <div v-if="open" class="params-overlay" @click.self="onClose">
      <div class="params-modal">
        <!-- Header -->
        <div class="modal-header">
          <h3>Parâmetros Globais</h3>
          <button class="btn" @click="onClose">Fechar</button>
        </div>

        <!-- Tabs -->
        <div class="tabs">
          <div class="tab" :class="{active: tab==='arena'}"      @click="tab='arena'">Arena</div>
          <div class="tab" :class="{active: tab==='obstaculos'}" @click="tab='obstaculos'">Obstáculos</div>
          <div class="tab" :class="{active: tab==='percurso'}"   @click="tab='percurso'">Percurso</div>
          <div class="tab" :class="{active: tab==='combos'}"     @click="tab='combos'">Combos</div>
          <div class="tab" :class="{active: tab==='visual'}"     @click="tab='visual'">Visual</div>
        </div>

        <!-- Body -->
        <div class="tab-body" v-if="tab==='arena'">
          <div class="row"><label>Largura padrão (m)</label>
            <input type="number" v-model.number="edit.arena.w" step="0.5">
          </div>
          <div class="row"><label>Altura padrão (m)</label>
            <input type="number" v-model.number="edit.arena.h" step="0.5">
          </div>
          <div class="row"><label>Snap grade (m)</label>
            <input type="number" v-model.number="edit.arena.snapGrid" step="0.05">
          </div>
          <div class="row"><label>Escala padrão (px/m)</label>
            <input type="number" v-model.number="edit.arena.scale" step="1">
          </div>

          <div class="row cb">
            <label>Modo livre: mostrar alças</label>
            <label class="pm-check">
              <input type="checkbox" v-model="edit.free.showHandles" />
              <span>Sim</span>
            </label>
          </div>

          <div class="row cb">
            <label>Modo livre: restringir obstáculos ao polígono</label>
            <label class="pm-check">
              <input type="checkbox" v-model="edit.free.confine" />
              <span>Sim</span>
            </label>
          </div>

          <div class="row cb">
            <label>Aplicar dimensões à arena atual</label>
            <label class="pm-check">
              <input type="checkbox" v-model="edit.arena.applyNow" />
              <span>Sim</span>
            </label>
          </div>
        </div>

        <div class="tab-body" v-else-if="tab==='obstaculos'">
          <!-- Subtabs com todos os tipos -->
          <div class="subtabs">
            <div
              v-for="opt in typeOptions"
              :key="opt.value"
              class="tab small"
              :class="{ active: typeTab === opt.value }"
              @click="typeTab = opt.value"
            >
              {{ opt.label }}
            </div>
          </div>

          <!-- Oxer / Triple: listas -->
          <template v-if="typeTab==='oxer' || typeTab==='tripleBar'">
            <div class="row"><label>Espessuras (m)</label>
              <input type="text"
                    :value="typeTab==='oxer' ? lists.oxerThicks : lists.tripleThicks"
                    @input="typeTab==='oxer' ? onListInput('oxerThicks',$event) : onListInput('tripleThicks',$event)"
                    @blur="typeTab==='oxer' ? onListBlur('oxerThicks') : onListBlur('tripleThicks')"
                    placeholder="0,10;0,10;0,10" />
            </div>

            <div class="row"><label>Alturas (m)</label>
              <input type="text"
                    :value="typeTab==='oxer' ? lists.oxerHeights : lists.tripleHeights"
                    @input="typeTab==='oxer' ? onListInput('oxerHeights',$event) : onListInput('tripleHeights',$event)"
                    @blur="typeTab==='oxer' ? onListBlur('oxerHeights') : onListBlur('tripleHeights')"
                    placeholder="0,60;0,80;1,00" />
            </div>

            <div class="row"><label>Vãos (m)</label>
              <input type="text"
                    :value="typeTab==='oxer' ? lists.oxerGaps : lists.tripleGaps"
                    @input="typeTab==='oxer' ? onListInput('oxerGaps',$event) : onListInput('tripleGaps',$event)"
                    @blur="typeTab==='oxer' ? onListBlur('oxerGaps') : onListBlur('tripleGaps')"
                    placeholder="0,50;0,60" />
            </div>

            <div class="apply-row">
              <button class="btn" @click="typeTab==='oxer' ? deriveOxerGaps() : deriveTripleGaps()">Derivar vãos a partir das alturas</button>
              <span v-if="typeTab==='oxer' && oxerError" class="hint warn">{{ oxerError }}</span>
              <span v-if="typeTab==='tripleBar' && tripleError" class="hint warn">{{ tripleError }}</span>
            </div>
          </template>

          <!-- Demais tipos: Largura, Espessura, Altura (exceto start/finish) -->
          <template v-else>
            <div class="row"><label>Largura (m)</label>
              <input type="number" step="0.05" v-model.number="edit.obstacles[typeTab].wm" />
            </div>
            <div class="row"><label>Espessura (m)</label>
              <input type="number" step="0.01" v-model.number="edit.obstacles[typeTab].barThickness" />
            </div>
            <div class="row" v-if="typeTab!=='start' && typeTab!=='finish'">
              <label>Altura (m)</label>
              <input type="number" step="0.05" v-model.number="edit.obstacles[typeTab].heights[0]" />
            </div>
          </template>

          <!-- Escopos de aplicação (mantidos) -->
          <div class="apply-row">
            <label class="checkbox"><input type="checkbox" v-model="flags.applySelected" /> <span>Aplicar ao selecionado</span></label>
            <label class="checkbox"><input type="checkbox" v-model="flags.applyType" /> <span>Aplicar a todos do tipo</span></label>
            <label class="checkbox"><input type="checkbox" v-model="flags.applyAll" /> <span>Aplicar a todos</span></label>
          </div>
        </div>

        <div class="tab-body" v-else-if="tab==='percurso'">
          <div class="row"><label>Curvatura (tension)</label>
            <input type="range" min="0" max="1" step="0.01" v-model.number="edit.path.pathTension" />
          </div>
          <div class="row"><label>Suavidade (smooth)</label>
            <input type="range" min="0" max="1" step="0.01" v-model.number="edit.path.curveSmoothness" />
          </div>
          <div class="row"><label>Passadas na saída</label>
            <input type="number" step="0.1" v-model.number="edit.path.exitStrides" />
          </div>
          <div class="row"><label>Passadas na entrada</label>
            <input type="number" step="0.1" v-model.number="edit.path.entryStrides" />
          </div>
          <div class="row"><label>Raio mínimo (m)</label>
            <input type="number" step="0.1" v-model.number="edit.path.rMin" />
          </div>
        </div>

        <div class="tab-body" v-else-if="tab==='combos'">
          <div class="row"><label>Centro a centro padrão (m)</label>
            <input type="number" step="0.1" v-model.number="edit.combos.defaultCentro" />
          </div>
          <div class="hint">Outros ajustes de combos podem ser adicionados depois.</div>
        </div>

        <div class="tab-body" v-else>
          <div class="row cb">
            <label>Mostrar régua nas bordas</label>
            <!-- checkbox com visual consistente -->
            <label class="pm-check">
              <input type="checkbox" v-model="edit.visual.showRulers" />
              <span>Sim</span>
            </label>
            <!-- mantemos a terceira coluna vazia pra não quebrar o grid .row.cb -->
            <div class="right-hint"></div>
          </div>

          <div class="row">
            <label>Passo da régua (m)</label>
            <div class="pm-range-wrap">
              <input
                type="range"
                min="1"
                max="20"
                step="1"
                v-model.number="edit.visual.rulerStep"
              />
              <span class="pm-chip">{{ rulerStepLabel }} m</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <button class="btn" @click="onSave('save')">Salvar</button>
          <button class="btn" @click="onSave('applySelected')">Salvar + Aplicar ao selecionado</button>
          <button class="btn" @click="onSave('applyType')">Salvar + Aplicar a todos do tipo</button>
          <button class="btn" @click="onSave('applyAll')">Salvar + Aplicar a todos</button>
        </div>
      </div>
    </div>
  </teleport>
</template>

<style scoped>
/* ===== Modal teletransportado (para <body>) ===== */
/* ===== THEME do modal (teleport) ===== */
.params-overlay {
  position: fixed !important;
  inset: 0 !important;
  z-index: 2147483647 !important;
  display: grid !important;
  place-items: center !important;
  background: rgba(2, 8, 23, .28) !important;
}

.params-modal {
  position: relative;
  width: min(960px, 94vw);
  max-height: 88vh;
  border-radius: 12px;
  background: #0f172a;      /* fundo sólido (escuro) */
  color: #e6edf7;
  box-shadow: 0 24px 64px rgba(0,0,0,.45);
  overflow: hidden;
}

/* Header / título / fechar */
.params-modal .modal-header{
  display:flex; justify-content:space-between; align-items:center;
  padding: 12px 14px 10px;
  border-bottom: 1px dashed #334;
}
.params-modal .modal-header h3{
  margin:0; font-size:22px; font-weight:700;
}

/* Tabs (pills) */
.params-modal .tabs{
  display:flex; gap:8px; flex-wrap:wrap;
  padding: 8px 14px;
}
.params-modal .tab{
  background:#1a2a4a; color:#e8eefc;
  border:1px solid #2a3a5a;
  padding:6px 12px; border-radius:999px;
  cursor:pointer; user-select:none;
}
.params-modal .tab.active{
  background:#22365e;
  box-shadow:0 0 0 2px #8ab6ff inset;
}

/* Corpo rolável */
.params-modal .tab-body, .params-modal .pm-body{
  padding: 10px 14px 2px;
  max-height: calc(88vh - 156px);  /* header + tabs + footer */
  overflow:auto;
}
.params-modal .tab-body::-webkit-scrollbar{ width:10px; height:10px; }
.params-modal .tab-body::-webkit-scrollbar-thumb{ background:#334155; border-radius:8px; }

/* Linhas de formulário */
.params-modal .tab-body .row{
  display:grid;
  grid-template-columns: 240px 1fr;     /* label | campo */
  gap:12px; align-items:center;
  margin-bottom:10px;
}
.params-modal .tab-body .row > label{
  justify-self:end; color:#cbd5e1; opacity:1;
}

/* Inputs e selects */
.params-modal input[type="number"],
.params-modal input[type="text"],
.params-modal select{
  width:100%;
  background:#0b1426;
  border:1px solid #2a3a5a;
  border-radius:8px;
  padding:8px 10px;
  color:#e8eefc;
}

/* === Checkbox unificado no modal (✓ centralizado) === */
/* === Checkbox unificado no modal (✓ correto) === */

/* Wrapper opcional de layout (pode manter onde já usa) */
.pm-check{ display:inline-flex; align-items:center; gap:8px; user-select:none; }
.pm-check span{ color:#e8eefc; font-size:13px; }

/* Dicas/legendas no corpo */
.params-modal .hint{ font-size:12px; opacity:.7; }

/* Footer com botões */
.params-modal .modal-footer{
  position:sticky; bottom:0; z-index:2;
  padding: 10px 14px 12px;
  border-top:1px solid rgba(148,163,184,.15);
  background: inherit;
  display:grid; grid-template-columns: repeat(4, 1fr); gap:8px;
}

/* Botões dentro do modal (não dependem de .montador) */
.params-modal .btn{
  background:#1a2a4a; color:#e8eefc;
  border:1px solid #2a3a5a; border-radius:10px;
  padding:8px 10px; cursor:pointer;
}
.params-modal .btn:hover{ background:#22365e; }
.params-modal .btn.danger{ background:#4a1a1a; border-color:#6a2a2a; }
.params-modal .btn.warn{ background:#3a2a10; border-color:#5a3a18; }

/* Variante: linha de checkbox com “Sim” à direita (se existir .right-hint) */
.params-modal .row.cb{
  grid-template-columns: 240px auto;  /* label | checkbox+Sim */
}
.params-modal .row.cb .right-hint{ display:none; }
/* ====== Slider consistente no modal ====== */

/* === Slider com chip à direita — reserva espaço p/ o valor === */
.pm-range-wrap { display: flex; align-items: center; gap: 10px; }

/* reserva ~80px p/ o chip (56px + gap) e evita a barra “esmurrar” o chip */
.pm-range-wrap input[type="range"]{
  flex: 1 1 auto;
  max-width: calc(100% - 80px);  /* <- ajuste fino se precisar de mais/menos espaço */
  min-width: 140px;              /* garante usabilidade em telas estreitas */
}

/* chip do valor (garante largura p/ "10,00 m") */
.pm-chip{
  min-width: 64px;               /* 64–72px é um bom range */
  text-align: center;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 12px;
  color: #0b1426;
  background: #a8d7ff;
  border: 1px solid rgba(255,255,255,.25);
}

/* === Checkbox consistente com ✓ centralizado === */

/* === Checkbox unificado em TODO o modal (✓ centralizado) === */
.params-modal input[type="checkbox"],
.params-modal .form-check-input[type="checkbox"]{
  -webkit-appearance: none !important;
  appearance: none !important;
  width: 18px; height: 18px;
  position: relative;
  margin: 0;
  border: 2px solid #3b82f6;         /* borda azul */
  border-radius: 4px;
  background: #0f172a !important;     /* mantém fundo escuro */
  box-shadow: none !important;
  outline: none;
  vertical-align: middle;
  display: inline-block;
  transition: border-color .12s ease;
}
/* desenha um ✓ correto (não “V” invertido) */
.params-modal input[type="checkbox"]::after,
.params-modal .form-check-input[type="checkbox"]::after{
  content: "";
  position: absolute;
  left: 50%; top: 50%;
  width: 6px;             /* haste curta do ✓ */
  height: 10px;           /* haste longa do ✓ */
  border: solid #3b82f6;
  border-width: 0 2px 2px 0;           /* canto ┘ */
  transform: translate(-50%, -55%) rotate(45deg) scale(0);
  transform-origin: center;
  transition: transform .12s ease;
}
.params-modal input[type="checkbox"]:checked::after,
.params-modal .form-check-input[type="checkbox"]::after{
  transform: translate(-50%, -55%) rotate(45deg) scale(1);
}


.pm-check{ display:inline-flex; align-items:center; gap:8px; user-select:none; }

.pm-check span{ color:#e8eefc; font-size:13px; }

/* Linha “aplicar em...” */
.params-modal .apply-row{
  display:flex; gap:16px; flex-wrap:wrap; margin-top: 8px;
}

.subtabs{
  display:flex; gap:8px; padding:0 14px 8px;
  flex-wrap: nowrap; overflow-x: auto;
}
.subtabs::-webkit-scrollbar{ height:6px; }
.subtabs::-webkit-scrollbar-thumb{ background:#334155; border-radius: 6px; }

</style>